import { useState, useEffect } from "react";
import axios from "axios";

const BASEURL = import.meta.env.VITE_REACT_APP_BACKEND_BASEURL;

// Mapeamento dos nomes das colunas
const columnLabels = {
  Points: "Pontos",
  Serves: "Saque",
  Attacks: "Ataque",
  Reception: "Recepção",
  Blocks: "BK",
  Total: "Tot",
  PB: "PB",
  WL: "WL",
  PointsPercent: "Pts%",
  Points: "Pts",
  Errors: "Err",
  Blocks: "BK",
  PosPercent: "Pos%",
  ExcPercent: "Exc%",
};

const groupOrder = ["Points", "Serves", "Attacks", "Reception", "Blocks"];
const subgroupOrder = {
  Points: ["Total", "PB", "WL"],
  Serves: ["Total", "Points", "Errors"],
  Attacks: ["Total", "Points", "Blocks", "Errors", "PointsPercent"],
  Reception: ["Total", "Errors", "PosPercent", "ExcPercent"],
  Blocks: [], // valor simples
};

const groupSeparators = [
  // índices onde termina cada grupo
  subgroupOrder.Points.length,
  subgroupOrder.Points.length + subgroupOrder.Serves.length,
  subgroupOrder.Points.length +
    subgroupOrder.Serves.length +
    subgroupOrder.Reception.length,
  subgroupOrder.Points.length +
    subgroupOrder.Serves.length +
    subgroupOrder.Reception.length +
    subgroupOrder.Attacks.length,
];

function ScoutAthletesTable({
  homeNumbers = [],
  awayNumbers = [],
  partida,
  set,
}) {
  const [homeData, setHomeData] = useState({});
  const [awayData, setAwayData] = useState({});
  const [loading, setLoading] = useState(false);

  // Função para substituir 0, null, undefined, "" ou "0" por hífen
  const formatValue = (val) => {
    if (
      val === 0 ||
      val === null ||
      val === undefined ||
      val === "" ||
      val === "0"
    )
      return "-";
    return val;
  };

  // Busca todos os dados dos atletas
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const homePromises = homeNumbers.map((num) =>
        axios
          .post(`${BASEURL}/reports/athletes-report-set`, {
            set: set ? parseInt(set) : undefined,
            partida,
            number: num,
            time: "*",
          })
          .then((res) => res.data)
          .catch(() => null)
      );
      const awayPromises = awayNumbers.map((num) =>
        axios
          .post(`${BASEURL}/reports/athletes-report-set`, {
            set: set ? parseInt(set) : undefined,
            partida,
            number: num,
            time: "a",
          })
          .then((res) => res.data)
          .catch(() => null)
      );
      const homeResults = await Promise.all(homePromises);
      const awayResults = await Promise.all(awayPromises);

      // Monta objeto: {numero: dados}
      const homeObj = {};
      homeNumbers.forEach((num, idx) => {
        homeObj[num] = homeResults[idx];
      });
      const awayObj = {};
      awayNumbers.forEach((num, idx) => {
        awayObj[num] = awayResults[idx];
      });
      setHomeData(homeObj);
      setAwayData(awayObj);
      setLoading(false);
    };
    if (partida && (homeNumbers.length > 0 || awayNumbers.length > 0)) {
      fetchAll();
    }
  }, [homeNumbers, awayNumbers, partida, set]);

  // Renderiza cabeçalho com separadores de grupo
  const renderTableHeader = () => (
    <thead>
      <tr>
        <th
          rowSpan={2}
          className="border border-gray-400 p-1 bg-gray-100 text-center"
        >
          #
        </th>
        {groupOrder.map((group, idx) =>
          subgroupOrder[group].length > 0 ? (
            <th
              key={group}
              colSpan={subgroupOrder[group].length}
              className={`border-t border-b border-gray-400 p-1 bg-gray-100 text-center border-l-4 ${
                idx === 0 ? "" : "border-l-gray-500"
              }`}
            >
              {columnLabels[group] || group}
            </th>
          ) : (
            <th
              key={group}
              rowSpan={2}
              className={`border-t border-b border-gray-400 p-1 bg-gray-100 text-center border-l-4 ${
                idx === 0 ? "" : "border-l-gray-500"
              }`}
            >
              {columnLabels[group] || group}
            </th>
          )
        )}
      </tr>
      <tr>
        {groupOrder.map((group, idx) =>
          subgroupOrder[group].length > 0
            ? subgroupOrder[group].map((sub, subIdx) => (
                <th
                  key={group + sub}
                  className={`border-b border-gray-400 p-1 bg-gray-50 text-center ${
                    subIdx === 0 && idx !== 0
                      ? "border-l-4 border-l-gray-500"
                      : ""
                  }`}
                >
                  {columnLabels[sub] || sub}
                </th>
              ))
            : null
        )}
      </tr>
    </thead>
  );

  // Renderiza linhas de atletas com zebra striping e separadores de grupo
  const renderRows = (dataObj, numbers, label) => {
    // Ordena e filtra números válidos
    const sortedNumbers = [...numbers]
      .filter((n) => n !== "" && n !== null && n !== undefined)
      .sort((a, b) => Number(a) - Number(b));
    return (
      <>
        <tr key={`group-title-${label}`}>
          <td colSpan={20} className="bg-gray-200 font-bold text-left p-2">
            {label}
          </td>
        </tr>
        {sortedNumbers.map((num, idx) => {
          const dados = dataObj[num] || {};
          return (
            <tr
              key={`${label}-${num}-${idx}`}
              className={idx % 2 === 1 ? "bg-gray-50" : ""}
            >
              <td className="border border-gray-400 p-1 text-center font-bold">
                {num}
              </td>
              {groupOrder.map((group, gIdx) =>
                subgroupOrder[group].length > 0 ? (
                  subgroupOrder[group].map((sub, subIdx) => {
                    let value = dados[group]?.[sub];
                    if (
                      group === "Points" &&
                      sub === "WL" &&
                      (value === "0" || value === 0)
                    )
                      value = "-";
                    value = formatValue(value);
                    const isHyphen = value === "-";
                    const borderClass =
                      subIdx === 0 && gIdx !== 0
                        ? "border-l-4 border-l-gray-500"
                        : "";
                    return (
                      <td
                        key={`${label}-${num}-${group}-${sub}`}
                        className={`border border-gray-400 p-1 text-center ${borderClass} ${
                          isHyphen ? "text-[10px]" : ""
                        }`}
                        style={{ minWidth: 32 }}
                      >
                        {value}
                      </td>
                    );
                  })
                ) : (
                  <td
                    key={`${label}-${num}-${group}`}
                    className={`border border-gray-400 p-1 text-center border-l-4 border-l-gray-500`}
                    style={{ minWidth: 32 }}
                  >
                    {formatValue(dados[group])}
                  </td>
                )
              )}
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <div className="overflow-x-auto">
      <h2 className="text-xl font-semibold mb-2">Scout do set {set}</h2>
      {loading ? (
        <div className="p-4">Carregando...</div>
      ) : (
        <table className="min-w-full border-collapse border border-gray-400 text-xs">
          {renderTableHeader()}
          <tbody>
            {renderRows(homeData, homeNumbers, "Home")}
            {renderRows(awayData, awayNumbers, "Away")}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ScoutAthletesTable;
